import { QueryCache, QueryClient } from "@tanstack/react-query";

import { SERVICE_UNAVAILABLE_EVENT } from "@/services/api";

/**
 * A failure looks like a service outage when it has no HTTP status (network /
 * DNS error, API fully down) or a gateway status (502–504). The API client
 * already raises {@link SERVICE_UNAVAILABLE_EVENT} for these on its own happy
 * path; the global handler below is the safety net so *any* query failure —
 * including ones that bypass the client's error path in future — still surfaces
 * the app-level banner after retries are exhausted.
 */
function isServiceUnavailable(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = (error as Error & { status?: number }).status;
  return status === undefined || (status >= 502 && status <= 504);
}

function notifyServiceUnavailable(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SERVICE_UNAVAILABLE_EVENT));
  }
}

/**
 * Create a QueryClient with the project-wide defaults. We embrace React Query's
 * fresher defaults — instant stale-while-revalidate (`staleTime: 0`), refetch on
 * window focus, and the default retry policy — so admin screens show cached data
 * immediately and refresh in the background.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (isServiceUnavailable(error)) notifyServiceUnavailable();
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    },
  });
}

export const queryClient = createQueryClient();
